import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/permissions";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Wrench, CheckCircle2, Calendar, Package, User, MapPin } from "lucide-react";

/**
 * Mobile-first technician view — a stripped-down task list of upcoming preventive-maintenance items.
 * Tap a task → mark completed with optional notes. Optimised for phones (single column, big touch targets).
 */
export default function TechnicianView() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // task being completed

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/maintenance/upcoming", { params: { limit: 30 } });
      // Assigned to me if performed_by matches, else show all scheduled
      setTasks(data);
    } catch { toast.error("Failed to load tasks"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const overdue = tasks.filter((t) => t.date < new Date().toISOString().slice(0, 10));
  const upcoming = tasks.filter((t) => t.date >= new Date().toISOString().slice(0, 10));

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-[#7C1D3A] text-white px-4 py-4">
        <div className="text-xs uppercase tracking-wider text-white/70">Technician View</div>
        <div className="font-heading text-2xl font-bold">Hi, {(user?.name || "").split(" ")[0]}</div>
        <div className="text-xs text-white/80 mt-1">{tasks.length} task{tasks.length === 1 ? "" : "s"} today · {overdue.length} overdue</div>
      </div>

      <div className="p-3 space-y-3" data-testid="technician-view">
        {loading ? (
          <div className="text-center py-8 text-slate-400 text-sm">Loading...</div>
        ) : tasks.length === 0 ? (
          <Card><CardContent className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
            <div className="font-heading text-lg font-semibold text-slate-900">All caught up!</div>
            <div className="text-sm text-slate-500 mt-1">No pending maintenance tasks.</div>
          </CardContent></Card>
        ) : (
          <>
            {overdue.length > 0 && (
              <>
                <div className="text-xs uppercase font-semibold text-red-700 mt-2 px-1">Overdue ({overdue.length})</div>
                {overdue.map((t) => <TaskCard key={t.id} task={t} onOpen={() => setActive(t)} accent="red" />)}
              </>
            )}
            {upcoming.length > 0 && (
              <>
                <div className="text-xs uppercase font-semibold text-slate-600 mt-3 px-1">Upcoming ({upcoming.length})</div>
                {upcoming.map((t) => <TaskCard key={t.id} task={t} onOpen={() => setActive(t)} accent="slate" />)}
              </>
            )}
          </>
        )}
      </div>

      {active && <CompleteTaskSheet task={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
}

function TaskCard({ task, onOpen, accent }) {
  const border = accent === "red" ? "border-red-200 bg-red-50/30" : "border-slate-200";
  return (
    <button onClick={onOpen} className={`w-full text-left rounded-xl border-2 ${border} p-4 bg-white active:scale-[0.98] transition`} data-testid={`task-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
            <Calendar className="w-3.5 h-3.5" /> {formatDate(task.date)}
            <Badge className="ml-2 uppercase text-[10px]">{task.type || "preventive"}</Badge>
          </div>
          <div className="font-heading text-base font-semibold text-slate-900 truncate">{task.asset_name || "Asset"}</div>
          <div className="text-xs text-slate-600 flex items-center gap-1"><Package className="w-3 h-3" />{task.asset_code || task.asset_id?.slice(0, 12)}</div>
          {task.description && <div className="text-sm text-slate-700 mt-2 line-clamp-2">{task.description}</div>}
        </div>
        <Wrench className="w-5 h-5 text-slate-400 shrink-0" />
      </div>
    </button>
  );
}

function CompleteTaskSheet({ task, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      // Assumes a maintenance-log update endpoint exists via PATCH /maintenance-logs/{id}
      await api.patch(`/maintenance-logs/${task.id}`, {
        status: "completed",
        completion_notes: notes,
        completed_at: new Date().toISOString(),
      });
      toast.success("Task marked complete");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-t-2xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto mb-2" />
        <div>
          <div className="font-heading text-lg font-bold text-slate-900">Mark as Completed</div>
          <div className="text-sm text-slate-600 mt-1">{task.asset_name} · {formatDate(task.date)}</div>
        </div>
        <Textarea rows={3} placeholder="What was done? (parts replaced, observations, etc.)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="task-completion-notes" />
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="flex-1 bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={submit} disabled={busy} data-testid="confirm-complete-task">
            <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "Saving..." : "Complete"}
          </Button>
        </div>
      </div>
    </div>
  );
}
